import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { FluentProvider } from '@fluentui/react-components';
import { themeForMode } from './ptcThemes';
import {
  ThemeContext,
  readStoredMode,
  resolveInitialMode,
  THEME_STORAGE_KEY,
  useThemeMode,
} from './themeMode';

/**
 * Theme provider. Resolution + the `useThemeMode` hook live in ./themeMode so
 * this file only exports React components (react-refresh convention).
 *
 * The active mode is projected onto :root as a `data-theme` attribute plus
 * `color-scheme`. index.css defines the shell's light values on `:root` and the
 * calibrated dark values under `:root[data-theme="dark"]`, so the plain-CSS
 * chrome (header, nav, cards, stat tiles) tracks the mode with no per-var JS.
 * index.html's pre-mount guard sets the same attribute before first paint, so a
 * dark visitor never sees a light flash while React loads.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState(resolveInitialMode);

  const setMode = useCallback((next: 'light' | 'dark') => {
    setModeState(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // persistence is best-effort; the in-memory mode still applies
    }
  }, []);

  const toggleMode = useCallback(() => {
    setMode(mode === 'dark' ? 'light' : 'dark');
  }, [mode, setMode]);

  // Live-follow OS theme changes until the user makes an explicit choice.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    if (typeof mql.addEventListener !== 'function') return;
    const onChange = (event: MediaQueryListEvent) => {
      if (readStoredMode() !== null) return; // explicit override in place
      setModeState(event.matches ? 'dark' : 'light');
    };
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  // Project the active mode onto :root. The attribute keys index.css's dark
  // variable block; color-scheme fixes native scrollbars, form controls, etc.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', mode);
    root.style.colorScheme = mode;
  }, [mode]);

  const value = useMemo(
    () => ({ mode, setMode, toggleMode }),
    [mode, setMode, toggleMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** FluentProvider bound to the active PTC theme (light/dark). */
export function ThemedFluentProvider({ children }: { children: ReactNode }) {
  const { mode } = useThemeMode();
  return <FluentProvider theme={themeForMode(mode)}>{children}</FluentProvider>;
}
